<script setup>
import { ref, computed, watch, getCurrentInstance } from 'vue';
import { usePage } from '@inertiajs/vue3';

const { proxy } = getCurrentInstance();
const $t = proxy.$t;

const props = defineProps({
    recipient: {
        type: Object,
        default: null
    },
    contentId: {
        type: [Number, String],
        required: true
    }
});

const emit = defineEmits(['change']);

const LOCALES = ['en', 'pt', 'es'];
const localeOptions = LOCALES.map(l => ({ label: l.toUpperCase(), value: l }));

const pageLocale = usePage().props.locale;
const emailLocale = ref(LOCALES.includes(pageLocale) ? pageLocale : 'en');

const defaultsFor = (locale) => {
    const messages = props.recipient?.default_messages || {};
    const owner = messages.site_owner?.[locale] || '';
    const seller = messages.external_seller?.[locale] || '';

    return {
        owner,
        seller,
        shared: props.recipient?.email_target === 'seller' ? seller : owner,
    };
};

const currentDefaults = computed(() => defaultsFor(emailLocale.value));

const sendToOwner = ref(props.recipient?.email_target === 'owner' && !!props.recipient?.owner_email);
const sendToSeller = ref(props.recipient?.email_target === 'seller' && !!props.recipient?.seller_email);
const sameMessage = ref(true);

const ownerMessage = ref(defaultsFor(emailLocale.value).owner);
const sellerMessage = ref(defaultsFor(emailLocale.value).seller);
const sharedMessage = ref(defaultsFor(emailLocale.value).shared);

const bothSelected = computed(() => sendToOwner.value && sendToSeller.value);
const showSharedEditor = computed(() => bothSelected.value && sameMessage.value);

// Switching the email language swaps untouched texts to the new language's
// default; texts already edited by the admin are kept as written.
watch(emailLocale, (newLocale, oldLocale) => {
    const oldDefaults = defaultsFor(oldLocale);
    const newDefaults = defaultsFor(newLocale);

    if (ownerMessage.value.trim() === oldDefaults.owner.trim()) {
        ownerMessage.value = newDefaults.owner;
    }
    if (sellerMessage.value.trim() === oldDefaults.seller.trim()) {
        sellerMessage.value = newDefaults.seller;
    }
    if (sharedMessage.value.trim() === oldDefaults.shared.trim()) {
        sharedMessage.value = newDefaults.shared;
    }
});

const restoreOwner = () => { ownerMessage.value = currentDefaults.value.owner; };
const restoreSeller = () => { sellerMessage.value = currentDefaults.value.seller; };
const restoreShared = () => { sharedMessage.value = currentDefaults.value.shared; };

const buildCustomMessage = (value, defaultValue) => {
    const text = (value || '').trim();
    if (!text || text === defaultValue.trim()) {
        return null;
    }
    return text;
};

// When leaving shared mode, carry any edited shared text into the
// per-recipient editors so the admin's writing is not silently dropped.
watch(showSharedEditor, (isShared, wasShared) => {
    if (wasShared && !isShared) {
        const text = (sharedMessage.value || '').trim();
        if (text && text !== currentDefaults.value.shared.trim()) {
            ownerMessage.value = sharedMessage.value;
            sellerMessage.value = sharedMessage.value;
        }
    }
});

const payload = computed(() => {
    let customOwner = null;
    let customSeller = null;

    if (showSharedEditor.value) {
        // Always send exactly what is displayed, so both recipients
        // receive the same body the admin sees in the shared editor.
        const shared = (sharedMessage.value || '').trim() || null;
        customOwner = shared;
        customSeller = shared;
    } else {
        if (sendToOwner.value) {
            customOwner = buildCustomMessage(ownerMessage.value, currentDefaults.value.owner);
        }
        if (sendToSeller.value) {
            customSeller = buildCustomMessage(sellerMessage.value, currentDefaults.value.seller);
        }
    }

    return {
        send_email_to_owner: sendToOwner.value,
        send_email_to_seller: sendToSeller.value,
        custom_message_owner: customOwner,
        custom_message_seller: customSeller,
        email_locale: emailLocale.value,
    };
});

watch(payload, (value) => emit('change', value), { immediate: true });
</script>

<template>
    <div class="space-y-3">
        <!-- Recipient checkboxes + email language -->
        <div class="flex flex-wrap items-center gap-6">
            <div class="flex items-center">
                <Checkbox
                    v-model="sendToOwner"
                    :binary="true"
                    :disabled="!recipient?.owner_email"
                    :inputId="'send-owner-' + contentId"
                />
                <label :for="'send-owner-' + contentId" class="ml-2 text-sm text-surface-700 cursor-pointer select-none">
                    {{ $t('Send email to owner') }}
                </label>
            </div>
            <div class="flex items-center">
                <Checkbox
                    v-model="sendToSeller"
                    :binary="true"
                    :disabled="!recipient?.seller_email"
                    :inputId="'send-seller-' + contentId"
                />
                <label :for="'send-seller-' + contentId" class="ml-2 text-sm text-surface-700 cursor-pointer select-none">
                    {{ $t('Send email to seller') }}
                </label>
            </div>
            <div v-if="bothSelected" class="flex items-center">
                <Checkbox
                    v-model="sameMessage"
                    :binary="true"
                    :inputId="'same-message-' + contentId"
                />
                <label :for="'same-message-' + contentId" class="ml-2 text-sm text-surface-700 cursor-pointer select-none">
                    {{ $t('Use the same message for both') }}
                </label>
            </div>
            <div v-if="sendToOwner || sendToSeller" class="flex items-center gap-2 ml-auto">
                <span class="text-sm text-surface-600">{{ $t('Email language') }}:</span>
                <SelectButton
                    v-model="emailLocale"
                    :options="localeOptions"
                    optionLabel="label"
                    optionValue="value"
                    :allowEmpty="false"
                />
            </div>
        </div>

        <!-- Message editors -->
        <div v-if="sendToOwner || sendToSeller" class="space-y-3">
            <!-- Shared message (both recipients, same message) -->
            <div v-if="showSharedEditor" class="p-4 bg-surface-50 rounded-lg border border-surface-200">
                <div class="flex items-center justify-between mb-2">
                    <label :for="'shared-message-' + contentId" class="text-sm font-medium text-surface-700">
                        {{ $t('Email message') }}
                    </label>
                    <Button
                        :label="$t('Restore default')"
                        icon="pi pi-refresh"
                        text
                        size="small"
                        severity="secondary"
                        @click="restoreShared"
                    />
                </div>
                <Textarea
                    :id="'shared-message-' + contentId"
                    v-model="sharedMessage"
                    rows="6"
                    autoResize
                    class="w-full"
                    maxlength="5000"
                />
                <p class="text-xs text-surface-500 mt-2">
                    {{ $t('This message replaces the email body. The article link and confirmation button are included automatically.') }}
                </p>
            </div>

            <template v-else>
                <!-- Owner message -->
                <div v-if="sendToOwner" class="p-4 bg-surface-50 rounded-lg border border-surface-200">
                    <div class="flex items-center justify-between mb-2">
                        <label :for="'owner-message-' + contentId" class="text-sm font-medium text-surface-700">
                            {{ $t('Message to owner') }}
                            <span v-if="recipient?.owner_email" class="text-surface-500 font-normal">({{ recipient.owner_email }})</span>
                        </label>
                        <Button
                            :label="$t('Restore default')"
                            icon="pi pi-refresh"
                            text
                            size="small"
                            severity="secondary"
                            @click="restoreOwner"
                        />
                    </div>
                    <Textarea
                        :id="'owner-message-' + contentId"
                        v-model="ownerMessage"
                        rows="6"
                        autoResize
                        class="w-full"
                        maxlength="5000"
                    />
                </div>

                <!-- Seller message -->
                <div v-if="sendToSeller" class="p-4 bg-surface-50 rounded-lg border border-surface-200">
                    <div class="flex items-center justify-between mb-2">
                        <label :for="'seller-message-' + contentId" class="text-sm font-medium text-surface-700">
                            {{ $t('Message to seller') }}
                            <span v-if="recipient?.seller_email" class="text-surface-500 font-normal">({{ recipient.seller_email }})</span>
                        </label>
                        <Button
                            :label="$t('Restore default')"
                            icon="pi pi-refresh"
                            text
                            size="small"
                            severity="secondary"
                            @click="restoreSeller"
                        />
                    </div>
                    <Textarea
                        :id="'seller-message-' + contentId"
                        v-model="sellerMessage"
                        rows="6"
                        autoResize
                        class="w-full"
                        maxlength="5000"
                    />
                </div>

                <p class="text-xs text-surface-500">
                    {{ $t('This message replaces the email body. The article link and confirmation button are included automatically.') }}
                </p>
            </template>
        </div>
    </div>
</template>
